/**
 * MobilePlaybookCreate Component
 *
 * Mobile page for creating a new playbook and linking it to a strategy.
 * Route: /settings/strategies/:strategyId/playbooks/new
 *
 * Uses atomic Dexie transaction to:
 * 1. Create playbook
 * 2. Link to strategy (update strategy.playbookIds)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Input,
  TextArea,
  Alert,
  useDisclosure,
  useToastNotification,
  Breadcrumbs,
  BreadcrumbItem,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';
import { db } from '@/lib/db';
import { playbookService } from '@/services/PlaybookService';
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { useStrategiesPlaybooks } from '../context';
import { UnsavedChangesDialog } from '../components';

export const MobilePlaybookCreate: React.FC = () => {
  const { strategyId } = useParams<{ strategyId: string }>();
  const navigate = useNavigate();
  const unsavedChangesDialog = useDisclosure();
  const { showToast } = useToastNotification();
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Context for data and post-creation state management
  const {
    strategies,
    loading,
    selectedStrategy,
    selectedStrategyId,
    showArchived,
    setShowArchived,
    selectStrategy,
    setShowArchivedPlaybooks,
    selectPlaybook,
    addPlaybookToList,
    updateSelectedStrategyPlaybookIds,
  } = useStrategiesPlaybooks();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [philosophy, setPhilosophy] = useState('');

  // Validation and submission state
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dirty state - any field has content
  const isDirty = name.trim() !== '' || description.trim() !== '' || philosophy.trim() !== '';

  // Validate and sync URL param to context on mount/change
  useEffect(() => {
    // Wait for strategies to load before any validation
    if (loading) {
      return;
    }

    if (!strategyId) {
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Validate against full strategies array (not filtered)
    const strategy = strategies.find((s) => s.id === strategyId);

    if (!strategy) {
      // Strategy not found - redirect to list
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Auto-enable showArchived if strategy is archived and filter is off
    if (strategy.status === 'archived' && !showArchived) {
      setShowArchived(true);
    }

    // Auto-enable showArchivedPlaybooks if strategy is archived
    if (strategy.status === 'archived') {
      setShowArchivedPlaybooks(true);
    }

    // Sync to context if not already selected
    if (selectedStrategyId !== strategyId) {
      selectStrategy(strategyId);
    }
  }, [strategyId, strategies, loading, selectedStrategyId, showArchived, setShowArchived, selectStrategy, setShowArchivedPlaybooks, navigate]);

  // Pending navigation target when showing unsaved changes dialog
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Handle navigation to Settings with dirty check
  const handleNavigateToSettings = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle navigation to Strategies with dirty check
  const handleNavigateToStrategies = useCallback(() => {
    if (isDirty) {
      setPendingNavigation('/settings/strategies');
      unsavedChangesDialog.onOpen();
    } else {
      navigate('/settings/strategies');
    }
  }, [isDirty, navigate, unsavedChangesDialog]);

  // Handle back navigation with dirty check
  const handleBack = useCallback(() => {
    const strategyPath = `/settings/strategies/${strategyId}`;
    if (isDirty) {
      setPendingNavigation(strategyPath);
      unsavedChangesDialog.onOpen();
    } else {
      navigate(strategyPath);
    }
  }, [isDirty, navigate, strategyId, unsavedChangesDialog]);

  // Handle discard from dialog
  const handleDiscard = useCallback(() => {
    unsavedChangesDialog.onClose();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [unsavedChangesDialog, navigate, pendingNavigation]);

  // Handle cancel button
  const handleCancel = useCallback(() => {
    if (isDirty) {
      unsavedChangesDialog.onOpen();
    } else {
      navigate(`/settings/strategies/${strategyId}`);
    }
  }, [isDirty, navigate, strategyId, unsavedChangesDialog]);

  // Handle name change - clear error when editing
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  }, [nameError]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      nameInputRef.current?.focus();
      return false;
    }
    setNameError(null);
    return true;
  }, [name]);

  // Handle form submission
  const handleCreate = useCallback(async () => {
    if (!strategyId || !selectedStrategy) {
      setSubmitError('Strategy not found');
      return;
    }

    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      const trimmedPhilosophy = philosophy.trim();

      // Build create payload
      const createData: Parameters<typeof playbookService.add>[0] = {
        name: trimmedName,
        status: 'active',
      };

      if (trimmedDescription) {
        createData.description = trimmedDescription;
      }

      if (trimmedPhilosophy) {
        createData.philosophy = trimmedPhilosophy;
      }

      // Use result variable to avoid shadowing (per GPT feedback)
      let result: { playbookId: string; updatedIds: string[] } | undefined;

      // Atomic transaction: create playbook + link to strategy
      // Include sync tables to avoid transaction scope mismatch
      await db.transaction('rw', [db.playbooks, db.strategies, db.sync_operations, db.sync_state], async () => {
        // 1. Create the playbook
        const newPlaybook = await playbookService.add(createData);

        // 2. Get current strategy to read existing playbookIds
        const strategy = await strategyService.get(strategyId);
        if (!strategy) {
          throw new Error('Strategy not found');
        }

        // 3. Update strategy with new playbook ID appended
        const updatedPlaybookIds = [...(strategy.playbookIds || []), newPlaybook.id];
        await strategyService.update(strategyId, { playbookIds: updatedPlaybookIds });

        result = { playbookId: newPlaybook.id, updatedIds: updatedPlaybookIds };
      });

      if (!result) {
        throw new Error('Transaction failed');
      }

      // Notify sync for both tables (after transaction commits)
      syncCoordinatorService.notifyChange('playbooks', result.playbookId);
      syncCoordinatorService.notifyChange('strategies', strategyId);

      // Get the created playbook for optimistic update
      const createdPlaybook = await playbookService.get(result.playbookId);
      if (createdPlaybook) {
        // Optimistic updates to avoid stale state
        addPlaybookToList(createdPlaybook);
        updateSelectedStrategyPlaybookIds(result.updatedIds);
      }

      // Prime context selection before navigation
      selectPlaybook(result.playbookId);

      showToast({
        title: 'Playbook Created',
        description: `"${trimmedName}" added to ${selectedStrategy.name}`,
        color: 'success',
      });

      navigate(`/settings/strategies/${strategyId}/playbooks/${result.playbookId}`);
    } catch (error) {
      console.error('Failed to create playbook:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create playbook';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    strategyId,
    selectedStrategy,
    name,
    description,
    philosophy,
    validateForm,
    addPlaybookToList,
    updateSelectedStrategyPlaybookIds,
    selectPlaybook,
    showToast,
    navigate,
  ]);

  // Show loading state while data loads
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-text-secondary">Loading...</span>
      </div>
    );
  }

  // Strategy not found handled by useEffect redirect
  if (!selectedStrategy) {
    return null;
  }

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleNavigateToStrategies}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Strategies
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleBack}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              {selectedStrategy.name}
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <h1 className="text-lg font-semibold text-text-primary mt-1">New Playbook</h1>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 px-1 pb-2">
        <Card>
          <CardBody className="space-y-4">
            {/* Name (required) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Name <span className="text-danger">*</span>
              </label>
              <Input
                ref={nameInputRef}
                placeholder="Enter playbook name"
                value={name}
                onValueChange={handleNameChange}
                isInvalid={!!nameError}
                errorMessage={nameError}
                autoFocus
                className="[&_input]:text-base"
              />
            </div>

            {/* Description (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Description
              </label>
              <TextArea
                placeholder="Brief description of this playbook..."
                value={description}
                onValueChange={setDescription}
                minRows={2}
                maxRows={4}
                className="[&_textarea]:text-base"
              />
            </div>

            {/* Philosophy (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Philosophy
              </label>
              <TextArea
                placeholder="Describe the trading philosophy for this playbook..."
                value={philosophy}
                onValueChange={setPhilosophy}
                minRows={3}
                maxRows={6}
                className="[&_textarea]:text-base"
              />
            </div>

            {/* Error Alert */}
            {submitError && (
              <Alert color="danger" variant="flat">
                {submitError}
              </Alert>
            )}
          </CardBody>
        </Card>
      </CardBody>

      {/* Footer */}
      <CardFooter className="flex items-center justify-end gap-1 pt-2 border-t border-default-200">
        <Button
          variant="flat"
          size="sm"
          onPress={handleCancel}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          onPress={handleCreate}
          isLoading={isSubmitting}
        >
          Create
        </Button>
      </CardFooter>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={unsavedChangesDialog.isOpen}
        onClose={unsavedChangesDialog.onClose}
        onDiscard={handleDiscard}
      />
    </Card>
  );
};
